<template>
  <b-sidebar
    id="sidebar-backdrop"
    backdrop-variant="dark"
    backdrop
    shadow
    right
    bg-variant="white"
    header-class="p-0"
    width="max(58%, 840px)"
    @shown="onShow"
    @hidden="onHidden"
  >
    <template #header="{ hide }">
      <SidebarHeader
        :isLoading="isLastAbfrageLoading || isFlugplanungsdatenLoading"
        :titleHref="'#/reisetermine/' + dataFromRow.reiseterminId"
        :kuerzel="dataFromRow?.reisetermin?.reiseterminkuerzel"
        :datum="
          dataFromRow?.deutscherAbflughafenIataCode ? '| ' + dataFromRow.deutscherAbflughafenIataCode : ''
        "
        @go-up="goUpOneRow"
        @go-down="goDownOneRow"
        @close="hide"
      >
        <template #actions>
          <b-button
            class="btn btn-icon icon-button"
            size="sm"
            v-b-tooltip.hover.bottom
            title="Neu laden"
            :disabled="isLastAbfrageLoading || isFlugplanungsdatenLoading || dataWasChanged"
            @click="onReload"
          >
            <RotateCcw :size="16" />
          </b-button>
          <b-button
            v-if="isAdminOrEntraUser"
            class="dialog-btn"
            variant="primary"
            size="sm"
            @click="showRequeryConfirmation"
          >
            Erneut abfragen
          </b-button>
        </template>
      </SidebarHeader>
      <b-modal centered title="ABFHT erneut abfragen" size="md" id="AbflughafenterminErneutAbfragen">
        <b-overlay>
          <p>
            Möchtest du
            <b>{{ dataFromRow?.reisetermin?.reiseterminkuerzel }}</b>
            |
            <b>{{ dataFromRow?.deutscherAbflughafenIataCode }}</b>
            erneut abfragen?
          </p>
        </b-overlay>
        <template #modal-footer="{ hide }">
          <b-button class="dialog-btn-secondary" @click="hide">Abbrechen</b-button>
          <b-button
            class="dialog-btn"
            variant="primary"
            v-b-toggle.sidebar-backdrop
            @click="requeryAbflughafentermin(hide, dataFromRow)"
          >
            Erneut abfragen
          </b-button>
        </template>
      </b-modal>
    </template>
    <b-overlay :show="isChangingBearbeitungsData" rounded="sm">
      <div class="sidebar-content" v-if="dataFromRow">
        <SidebarFieldSection header="Übersicht" :value="rasterItems(dataOverview)" />

        <SidebarSection class="mt-10" title="Bearbeitung">
          <template #actions>
            <b-button class="dialog-btn" variant="danger" size="sm" @click="resetBearbeitungsData">
              Reset
            </b-button>
            <b-button
              class="btn btn-icon icon-button"
              size="sm"
              v-b-tooltip.hover.bottom
              title="Aufgabe erstellen"
              v-b-modal.modalAufgabeEdit
              @click="openModalWithData"
            >
              <ListChecks :size="16" />
            </b-button>
            <b-button
              class="dialog-btn"
              size="sm"
              :variant="disableSichernButton ? 'secondary' : 'primary'"
              :disabled="disableSichernButton"
              @click="changeBearbeitungsData"
            >
              Sichern
            </b-button>
          </template>
          <FVCBearbeitung
            :data="dataBearbeitung"
            :last="!istAutomatischUebernommen"
            v-on:data-was-changed="changeFormVariables"
          ></FVCBearbeitung>
          <div v-if="istAutomatischUebernommen" class="fvc-hinweis">
            <TriangleAlert :size="16" />
            <span>Die Daten wurden von der letzten Abfrage automatisch übernommen.</span>
          </div>
        </SidebarSection>

        <SidebarFieldSection
          class="mt-10"
          header="Reisetermin"
          :value="rasterItems(dataReisetermin)"
          collapsedOnMount
        />

        <SidebarFieldSection
          class="mt-10"
          header="Flugplanungsdaten"
          :value="rasterItems(dataFlugplanung, true)"
        />

        <SidebarFieldSection
          v-if="keyCountLastAbfrage > 0 || isLastAbfrageLoading"
          class="mt-10"
          header="Vorherige Buchungsantwort"
          :value="rasterItems(dataLastAbfrage, true)"
        />
        <SidebarSection v-else class="mt-10" title="Vorherige Buchungsantwort">
          <div class="fvc-leer">Kein Ergebnis gefunden.</div>
        </SidebarSection>

        <SidebarFieldSection
          v-if="dataFromRow.info !== null"
          class="mt-10"
          header="Info"
          :value="rasterItems(dataInfo)"
        />
        <SidebarSection v-else class="mt-10" title="Info">
          <div class="fvc-leer">Kein Ergebnis gefunden.</div>
        </SidebarSection>

        <SidebarSection class="mt-10" title="Buchungsantworten Historie" :collapsible="false" padded>
          <LineChart :datapoints="dataLastFiveAbfragen"></LineChart>
        </SidebarSection>

        <SidebarSection
          ref="aufgabenSection"
          class="mt-10"
          title="Aufgaben"
          :card="false"
          collapsedOnMount
          @toggle="onAufgabenToggle"
        >
          <AufgabenWidget
            :filter="'sidebar-flugplanung'"
            :ansicht="'sidebar-flugplanung'"
            :aufgaben="this.aufgaben.values || []"
          ></AufgabenWidget>
          <div v-if="aufgaben.values.length === 0" class="fvc-leer">Kein Ergebnis gefunden.</div>
        </SidebarSection>
      </div>
    </b-overlay>
  </b-sidebar>
</template>

<script>
const moment = require('moment');
import buildQuery from 'odata-query';
import FVCBearbeitung from '@/components/flugverfuegbarkeit/fvc-bearbeitung.vue';
import AufgabenWidget from '@/components/produkte/reisetermine/aufgaben-widget.vue';
import { format, sub } from 'date-fns';

import { mapState, mapGetters } from 'vuex';
import { translateStatusGruppeOptions, translateEinkaufsartOptions } from '@/core/common/helpers/utils.js';
import apiService from '@/core/common/services/api.service';
import {
  ursachenPossibilities,
  loesungsansatzOptions,
  allAkzeptanzgruende,
  editStatusOptions,
} from '@/core/flugverfuegbarkeit/bearbeitungs-options.config';
import {
  SET_DATAWASCHANGED,
  RESET_LAST_ABFRAGE,
  SET_BEARBEITUNGSMODUS,
  SET_DETAILDATA,
  RESET_FLUGPLANUNGSDATA,
  RESET_LAST_FIVE_ABFRAGEN,
} from '@/core/flugverfuegbarkeit/stores/flugverfuegbarkeit.module';
import LineChart from '@/components/flugverfuegbarkeit/line-chart.vue';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import { CircleCheck, ListChecks, TriangleAlert, CircleX, RotateCcw } from 'lucide-vue';
import { lucideHtml } from '@/core/common/helpers/lucide-html';
import SidebarHeader from '@/components/common/sidebar-section/sidebar-header.vue';
import SidebarSection from '@/components/common/sidebar-section/sidebar-section.vue';
import SidebarFieldSection from '@/components/common/sidebar-section/sidebar-field-section.vue';

export default {
  props: {
    lastAbfrage: { type: Object, default: null },
    updateChangedRow: { type: Function },
  },
  data() {
    return {
      deutscherAbflughafen: null,
      dataForOverview: null,
      dataForReisetermin: null,
      formVariables: null,
      isOverviewCollapsed: true,
      isReiseterminCollapsed: false,
      isFlugplanungsdatenCollapsed: true,
      isBearbeitungCollapsed: true,
      isLastAbfrageCollapsed: true,
      isInfoCollapsed: true,
      bearbeitungStatusOptions: editStatusOptions,
      isChangingBearbeitungsData: false,
      isAufgabenCollapsed: false,
      aufgaben: { values: [], isLoaded: false },
    };
  },
  components: {
    ListChecks,
    TriangleAlert,
    RotateCcw,
    SidebarHeader,
    SidebarSection,
    SidebarFieldSection,
    FVCBearbeitung,
    LineChart,
    AufgabeErstellenModal,
    AufgabenWidget,
  },
  computed: {
    ...mapState({
      bearbeitungsModus: state => state.flugverfuegbarkeit.bearbeitung.bearbeitungsModus,
      dataFromRow: state => state.flugverfuegbarkeit.detailData,
      dataWasChanged: state => state.flugverfuegbarkeit.bearbeitung.dataWasChanged,
      flugplanungsData: state => state.flugverfuegbarkeit.flugplanungsData,
      isLastAbfrageLoading: state => state.flugverfuegbarkeit.isLastAbfrageLoading,
      isFlugplanungsdatenLoading: state => state.flugverfuegbarkeit.isFlugplanungsdatenLoading,
      lastFiveAbfragen: state => state.flugverfuegbarkeit.lastFiveAbfragen,
    }),
    ...mapGetters(['parsedRoles', 'currentUserId']),

    akzeptanzgrundOptions() {
      if (this.lastAbfrage.status) {
        return allAkzeptanzgruende;
      } else return null;
    },
    keyCountLastAbfrage() {
      return Object.keys(this.lastAbfrage).length || 0;
    },
    dataBearbeitung() {
      if (Object.keys(this.dataFromRow).length > 0) {
        return {
          ursache: this.dataFromRow['ursache'],
          beschreibung: this.dataFromRow['beschreibung'],
          loesungsansatz: this.dataFromRow['loesungsansatz'],
          akzeptanzgrund: this.dataFromRow['akzeptanzgrund'],
          status: this.dataFromRow['status'],
          bearbeitendePerson: this.dataFromRow['lastModifiedUserId'],
        };
      } else return {};
    },
    dataFlugplanung() {
      return [
        {
          label: 'Status Gruppe',
          value:
            this.dataFromRow?.reisetermin?.flugeinkauf?.statusgruppe != null
              ? this.translateStatusGruppeOptions(this.dataFromRow.reisetermin.flugeinkauf.statusgruppe)
              : '-',
          renderer: null,
          variant: null,
        },
        {
          label: 'Einkaufsart',
          value:
            this.dataFromRow?.reisetermin?.flugeinkauf?.einkaufsart != null
              ? this.translateEinkaufsartOptions(this.dataFromRow.reisetermin.flugeinkauf.einkaufsart)
              : '-',
          renderer: null,
          variant: null,
        },
        {
          label: 'Optionsfrist / Datum',
          value:
            `${
              this.dataFromRow?.reisetermin?.flugeinkauf?.optionsfrist != null
                ? this.dataFromRow.reisetermin.flugeinkauf.optionsfrist
                : 0
            } Tage / ` +
            (this.dataFromRow?.reisetermin?.abreisedatum != null &&
            this.dataFromRow?.reisetermin?.flugeinkauf?.optionsfrist != null
              ? format(
                  sub(new Date(this.dataFromRow.reisetermin.abreisedatum), {
                    days: this.dataFromRow.reisetermin.flugeinkauf.optionsfrist,
                  }),
                  'dd.MM.yyyy'
                )
              : '-'),
          renderer: null,
          variant: null,
        },
        {
          label: 'GRP Flughafen',
          value:
            this.dataFromRow?.reisetermin?.flugeinkauf?.gruppeFlughaefen != null
              ? this.dataFromRow.reisetermin.flugeinkauf.gruppeFlughaefen.join(', ')
              : '-',
          renderer: null,
          variant: null,
        },
      ];
    },
    dataOverview() {
      if (Object.keys(this.dataFromRow).length > 0) {
        return [
          {
            label: 'Abfragezeitpunkt',
            value: this.dataFromRow['abfragedatum'],
            renderer: this.dateFormatterWithHours(this.dataFromRow['abfragedatum']),
            variant: null,
          },
          {
            label: 'Zuletzt abgefragt',
            value: this.dataFromRow['blankImportedDateUtc'],
            renderer: this.dateFormatterWithHours(this.dataFromRow['blankImportedDateUtc']),
            variant: null,
          },
          {
            label: 'Status',
            value: this.dataFromRow['status'],
            renderer: `<h6 class="mb-0"><span class="kachel-badge ${
              this.dataFromRow['status'] === 'Verfuegbar'
                ? 'green'
                : this.dataFromRow['status'] === 'NichtVerfuegbar'
                ? 'red'
                : 'yellow'
            }">${
              this.dataFromRow['status'] === 'Verfuegbar'
                ? 'J'
                : this.dataFromRow['status'] === 'NichtVerfuegbar'
                ? 'N'
                : 'RQ'
            }</span></h6>`,
            variant: null,
          },
          {
            label: 'Aufpreis',
            value: this.dataFromRow['aufpreis'] !== null ? this.dataFromRow['aufpreis'] + '€' : '',
            renderer: `<h6 class="mb-0"><span class="kachel-badge ${
              this.dataFromRow['aufpreis'] === null
                ? 'gray'
                : this.dataFromRow['aufpreis'] > 200
                ? 'red'
                : 'green'
            }">${
              this.dataFromRow['aufpreis'] !== null ? this.dataFromRow['aufpreis'] + '€' : '-'
            }</span></h6>`,
          },
          {
            label: 'Fehlermeldung',
            value: this.dataFromRow['abfragefehler']?.text || '-',
            renderer: this.fehlerRenderer(this.dataFromRow['abfragefehler']),
            variant: null,
          },
          {
            label: 'Flugstamm Hinflug',
            value: this.dataFromRow['flugstammHinflug'] || '-',
            renderer: null,
            variant: null,
          },
          {
            label: 'Zimmertyp',
            value: this.dataFromRow['zimmertyp'] || '-',
            renderer: null,
            variant: null,
          },
          {
            field: 'reisetermin.thLangstrecke',
            label: 'TH Wert',
            renderer: this.dataFromRow.reisetermin.thLangstrecke
              ? `<h6 class="my-1">${this.dataFromRow.reisetermin.thLangstrecke + '€'}</h6>`
              : '<h6 class="my-1">-</h6>',
            variant: null,
          },
        ];
      } else return null;
    },
    dataReisetermin() {
      if (Object.keys(this.dataFromRow).length > 0) {
        return [
          {
            label: 'Priorität',
            value: this.dataFromRow['reisetermin']['reise']['prioritaet'],
            renderer: 'prioritaetWidget',
            variant: null,
          },

          {
            label: 'Hotelstart',
            value: this.dataFromRow['reisetermin']['startdatum'],
            renderer: this.dateFormatterStandard(this.dataFromRow['reisetermin']['startdatum']),
            variant: null,
          },
          {
            label: 'Ab- / Rückreisedatum',
            value: null,
            renderer: `<h6 class="my-1">${
              this.dateFormatterStandard(this.dataFromRow['reisetermin']['abreisedatum'], false) +
              ' / ' +
              this.dateFormatterStandard(this.dataFromRow['reisetermin']['hotelendUndRueckflugdatum'], false)
            }</h6>`,
            variant: null,
          },
          {
            label: 'Zielland Ab- / Zielflughäfen',
            value: null,
            renderer: `<h6 class="my-1">${
              this.dataFromRow['zielflughafenIataCode'] + ' / ' + this.dataFromRow['abflughafenIataCode']
            }</h6>`,
            variant: null,
          },

          {
            label: 'Abgesagt / Garantiert / Stop Sale',
            value: null,
            renderer: `<h6 class="my-1">${
              this.dataFromRow['reisetermin']['abgesagt']
                ? lucideHtml(CircleCheck, { class: 'text-success' })
                : lucideHtml(CircleX, { class: 'text-danger' })
            } / ${
              this.dataFromRow['reisetermin']['garantiert']
                ? lucideHtml(CircleCheck, { class: 'text-success' })
                : lucideHtml(CircleX, { class: 'text-danger' })
            } / ${
              this.dataFromRow['reisetermin']['stopSale']
                ? lucideHtml(CircleCheck, { class: 'text-success' })
                : lucideHtml(CircleX, { class: 'text-danger' })
            }</h6>`,
            variant: null,
          },
          {
            label: 'Pax',
            value: this.dataFromRow['reisetermin'],
            renderer: 'paxWidget',
            variant: null,
          },
        ];
      } else return null;
    },
    dataLastAbfrage() {
      if (this.isLastAbfrageLoading) {
        return [
          {
            label: 'Abfragezeitpunkt',
            renderer: '<div class="spinner spinner-dark"></div>',
            variant: null,
          },
          {
            label: 'Status',
            renderer: '<div class="spinner spinner-dark"></div>',
            variant: null,
          },
          {
            label: 'Aufpreis',
            renderer: '<div class="spinner spinner-dark"></div>',
            variant: null,
          },
        ];
      } else if (this.lastAbfrage) {
        if (!this.lastAbfrage.bearbeitungStatus) {
          return [
            {
              label: 'Abfragezeitpunkt',
              value: this.lastAbfrage['abfragedatum'],
              renderer: this.dateFormatterWithHours(this.lastAbfrage['abfragedatum']),
              variant: null,
            },
            {
              label: 'Status',
              value: this.lastAbfrage['status'],
              renderer: `<h6 class="m-0"><span class="kachel-badge ${
                this.lastAbfrage['status'] === 'Verfuegbar'
                  ? 'green'
                  : this.lastAbfrage['status'] === 'NichtVerfuegbar'
                  ? 'red'
                  : 'yellow'
              }">${
                this.lastAbfrage['status'] === 'Verfuegbar'
                  ? 'J'
                  : this.lastAbfrage['status'] === 'NichtVerfuegbar'
                  ? 'N'
                  : 'RQ'
              }</span></h6>`,
              variant: null,
            },
            {
              label: 'Aufpreis',
              value: this.dataFromRow['aufpreis'] !== null ? this.dataFromRow['aufpreis'] + '€' : '',
              renderer: `<h6 class="mb-0"><span class="kachel-badge ${
                this.dataFromRow['aufpreis'] === null
                  ? 'gray'
                  : this.dataFromRow['aufpreis'] > 200
                  ? 'red'
                  : 'green'
              }">${
                this.dataFromRow['aufpreis'] !== null ? this.dataFromRow['aufpreis'] + '€' : '-'
              }</span></h6>`,
            },
            {
              label: 'Fehlermeldung',
              value: this.lastAbfrage['abfragefehler']?.text || null,
              renderer: this.fehlerRenderer(this.lastAbfrage['abfragefehler']),
              variant: null,
            },
          ];
        } else
          return [
            {
              label: 'Abfragezeitpunkt',
              value: this.lastAbfrage['abfragedatum'],
              renderer: this.dateFormatterWithHours(this.lastAbfrage['abfragedatum']),
              variant: null,
            },
            {
              label: 'Status',
              value: this.lastAbfrage['status'],
              renderer: `<h6 class="m-0"><span class="kachel-badge ${
                this.lastAbfrage['status'] === 'Verfuegbar'
                  ? 'green'
                  : this.lastAbfrage['status'] === 'NichtVerfuegbar'
                  ? 'red'
                  : 'yellow'
              }">${
                this.lastAbfrage['status'] === 'Verfuegbar'
                  ? 'J'
                  : this.lastAbfrage['status'] === 'NichtVerfuegbar'
                  ? 'N'
                  : 'RQ'
              }</span></h6>`,
              variant: null,
            },
            {
              label: 'Aufpreis',
              value: this.dataFromRow['aufpreis'] !== null ? this.dataFromRow['aufpreis'] + '€' : '',
              renderer: `<h6 class="mb-0"><span class="kachel-badge ${
                this.dataFromRow['aufpreis'] === null
                  ? 'gray'
                  : this.dataFromRow['aufpreis'] > 200
                  ? 'red'
                  : 'green'
              }">${
                this.dataFromRow['aufpreis'] !== null ? this.dataFromRow['aufpreis'] + '€' : '-'
              }</span></h6>`,
            },
            {
              label: 'Fehlermeldung',
              value: this.lastAbfrage['abfragefehler']?.text || null,
              renderer: this.fehlerRenderer(this.lastAbfrage['abfragefehler']),
              variant: null,
            },
            {
              label: 'Bearbeitungsstatus',
              value: this.lastAbfrage.bearbeitungStatus
                ? this.getBearbeitungsstatus(this.lastAbfrage.bearbeitungStatus)?.label
                : null,
              variant: null,
              renderer: null,
            },
            {
              label: 'Ursachen',
              value: this.lastAbfrage.ursache ? this.getUrsache(this.lastAbfrage.ursache)?.label : null,
              variant: null,
              renderer: null,
            },
            {
              label: 'Beschreibung',
              renderer: null,
              variant: null,
              value: this.lastAbfrage.beschreibung ? this.lastAbfrage.beschreibung : null,
            },
            {
              label: 'Lösungsansatz',
              renderer: null,
              value: this.lastAbfrage.loesungsansatz
                ? this.getLoesungsansatz(this.lastAbfrage.loesungsansatz)
                : null,
              variant: null,
            },
            {
              label: 'Akzeptanzgrund',
              renderer: null,
              variant: null,
              value: this.lastAbfrage.akzeptanzgrund
                ? this.getAkzeptanzgrund(this.lastAbfrage.akzeptanzgrund)
                : null,
            },
            {
              label: 'Bearbeitende Person',
              renderer: null,
              variant: null,
              value: this.lastAbfrage.lastModifiedUserId
                ? this.$store.getters.getUsernameById(this.lastAbfrage.lastModifiedUserId)
                : '-',
            },
          ];
      } else return null;
    },
    dataInfo() {
      if (this.dataFromRow.info !== null) {
        return [
          {
            label: 'Info 3',
            value: this.dataFromRow.info?.info3,
            renderer: null,
            variant: null,
          },
          {
            label: 'Info 4',
            value: this.dataFromRow.info?.info4,
            renderer: null,
            variant: null,
          },
          {
            label: 'Info 5',
            value: this.dataFromRow.info?.info5,
            renderer: null,
            variant: null,
          },
          {
            label: 'Info 6',
            value: this.dataFromRow.info?.info6,
            renderer: null,
            variant: null,
          },
          {
            label: 'Info 7',
            value: this.dataFromRow.info?.info7,
            renderer: null,
            variant: null,
          },
          {
            label: 'Info 8',
            value: this.dataFromRow.info?.info8,
            renderer: null,
            variant: null,
          },
        ];
      } else return null;
    },
    dataLastFiveAbfragen() {
      if (!this.isLastAbfrageLoading) {
        if (this.lastFiveAbfragen.length > 0) {
          return this.lastFiveAbfragen.map(item => ({
            status: item.status,
            aufpreis: item.aufpreis < 0 ? 0 : item.aufpreis,
            abfragedatum: this.dateFormatterStandard(item.abfragedatum, false),
            variant:
              item.status === 'NichtVerfuegbar'
                ? 'danger'
                : item.status === 'Verfuegbar'
                ? 'success'
                : 'warning',
            height: item.aufpreis ? 50 + Math.max(0, item.aufpreis) / 10 + 'px' : '50px',
            statusLabel: item.status === 'NichtVerfuegbar' ? 'N' : item.status === 'Verfuegbar' ? 'J' : 'RQ',
          }));
        } else return [];
      } else {
        return [];
      }
    },
    disableSichernButton() {
      const akzeptiertStatusAndAkzeptanzgrundNotSelected =
        this.formVariables?.statusSelected === 'Akzeptiert' &&
        this.formVariables?.akzeptanzgrundSelected === null;
      return (
        !this.dataWasChanged ||
        akzeptiertStatusAndAkzeptanzgrundNotSelected ||
        this.formVariables?.statusSelected === null
      );
    },
    istAutomatischUebernommen() {
      return this.dataFromRow?.lastModifiedUserId === null && this.dataFromRow?.bearbeitungStatus !== null;
    },
    isAdminOrEntraUser() {
      return (
        this.parsedRoles.includes('Admin') ||
        this.currentUserId === '61ca51a5-ee49-4bd9-83a4-2e7f7f2ac67b' ||
        this.currentUserId === 'cb0edf20-ee76-44ae-8f88-8ecd7db1a22f' ||
        this.currentUserId === '3379fe89-2b55-4fe4-8a83-4f2e858d16e8' ||
        this.currentUserId === '3c7a8886-e086-44f0-bac5-a92e858411aa'
      );
    },
  },
  methods: {
    translateStatusGruppeOptions,
    translateEinkaufsartOptions,
    // Hebt eine Fehlermeldung farblich hervor, der Text wird für v-html maskiert
    fehlerRenderer(fehler) {
      if (!fehler?.text) {
        return null;
      }
      const text = fehler.text.replace(
        /[&<>"']/g,
        zeichen => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[zeichen])
      );
      return `<span class="fvc-fehler">${text}</span>`;
    },
    // Blendet optional Felder ohne Wert aus und verteilt die übrigen abwechselnd auf beide Spalten
    rasterItems(items, ohneLeere = false) {
      let spalte = 0;
      return (items || []).map(item => {
        const notShow = ohneLeere && item.value === null;
        if (notShow || item.full) {
          return { ...item, notShow };
        }
        spalte++;
        return { ...item, notShow, column: ((spalte - 1) % 2) + 1 };
      });
    },
    showRequeryConfirmation() {
      this.$bvModal.show('AbflughafenterminErneutAbfragen');
    },
    async requeryAbflughafentermin(hide, data) {
      hide();

      await apiService.put('/flugverfuegbarkeit/requery', [data.abfragedatum]).then(res => {
        this.toast('Erfolgreich erneut abgefragt');
      });
    },
    // Der Aufgaben-Cache wird verworfen und bei offener Sektion neu geladen, die Zeile lädt die Seite neu
    onReload() {
      this.aufgaben = { values: [], isLoaded: false };
      if (this.isAufgabenCollapsed) {
        this.onAufgabenToggle(true);
      }
      this.$emit('reload');
    },
    async onAufgabenToggle(isOpen) {
      this.isAufgabenCollapsed = isOpen;

      if (isOpen && !this.aufgaben.isLoaded) {
        const odataFilter = buildQuery({
          filter: {
            flugverfuegbarkeiten: {
              any: {
                id: this.dataFromRow.id,
              },
            },
          },
        });
        this.aufgaben.values = await apiService.get('/aufgaben' + odataFilter).then(response => {
          return response.data.result.value;
        });
        this.aufgaben.isLoaded = true;
      }
    },
    openModalWithData() {
      this.$emit('openAufgabeErstellenModal', [
        { id: 0, typ: 'Flugverfügbarkeit', artifactId: this.dataFromRow.id, artifactText: null },
      ]);
      this.$bvModal.show('modalAufgabeEdit');
    },
    async resetBearbeitungsData() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Willst du den Bearbeitungsstatus wirklich leeren?',
        {
          title: 'Bearbeitungsstatus leeren',
          footerClass: 'modal-footer--centered',
          centered: true,
          okTitle: 'Löschen',
          cancelTitle: 'Abbrechen',
          okVariant: 'danger',
        }
      );
      if (confirmed) {
        await apiService.put('flugverfuegbarkeit/resetStatusData?id=' + this.dataFromRow.id).then(() => {
          this.$emit('dataChangeSuccessful', this.dataFromRow.id);
        });
      }
    },
    onShow() {
      this.addKeyEventListener();
    },
    addKeyEventListener() {
      window.addEventListener('keydown', this.handleKeyPress);
    },
    removeKeyEventListener() {
      window.removeEventListener('keydown', this.handleKeyPress);
    },
    onHidden() {
      this.$emit('hidden');
      this.removeKeyEventListener();
      this.clearData();
      this.resetStoreData();
    },
    clearData() {
      this.dataForOverview = null;
      this.dataForReisetermin = null;
      this.isAufgabenCollapsed = false;
      this.$refs.aufgabenSection?.close();
      this.aufgaben = { values: [], isLoaded: false };
    },
    resetStoreData() {
      this.$store.commit('flugverfuegbarkeit/' + SET_BEARBEITUNGSMODUS, false);
      this.$store.commit('flugverfuegbarkeit/' + SET_DETAILDATA, {});
      this.$store.commit('flugverfuegbarkeit/' + RESET_FLUGPLANUNGSDATA);
      this.$store.commit('flugverfuegbarkeit/' + RESET_LAST_ABFRAGE);
      this.$store.commit('flugverfuegbarkeit/' + RESET_LAST_FIVE_ABFRAGEN);
    },

    dateFormatterWithHours(date, withH6Wrapper = true) {
      const formattedDate = moment(date, moment.ISO_8601).format('DD.MM.YYYY HH:mm');
      if (!withH6Wrapper) {
        return formattedDate;
      } else return `<h6 class="my-1">${formattedDate}</h6>`;
    },
    dateFormatterStandard(date, withH6Wrapper = true) {
      const formattedDate = moment(date, moment.ISO_8601).format('DD.MM.YYYY');
      if (!withH6Wrapper) {
        return formattedDate;
      } else return `<h6 class="my-1">${formattedDate}</h6>`;
    },
    switchBearbeitungsmodus() {
      this.$store.commit('flugverfuegbarkeit/' + SET_BEARBEITUNGSMODUS, !this.bearbeitungsModus);
    },
    async changeBearbeitungsData() {
      if (this.formVariables && this.dataWasChanged) {
        const ursachen = this.formVariables.ursacheSelected || null;
        const bearbeitungStatus = this.formVariables.statusSelected || null;
        const loesungsansatz = this.formVariables.loesungsansatzSelected || null;
        const akzeptanzgrund = this.formVariables.akzeptanzgrundSelected || null;
        this.isChangingBearbeitungsData = true;

        const params = {
          id: this.dataFromRow.id,
          ursachen,
          bearbeitungStatus,
          beschreibung: this.formVariables.beschreibungText,
          loesungsansatz,
          akzeptanzgrund,
        };

        if (bearbeitungStatus !== null) {
          await apiService
            .put('flugverfuegbarkeit/updateStatusData?id=' + this.dataFromRow.id, params)
            .then(() => {
              this.$store.commit('flugverfuegbarkeit/' + SET_DATAWASCHANGED, false);
              this.toast('Flugverfügbarkeit wurde erfolgreich bearbeitet.');
              this.$emit('dataChangeSuccessful', this.dataFromRow.id);
              this.$store.commit('flugverfuegbarkeit/' + SET_BEARBEITUNGSMODUS, false);
            })
            .catch(error => {
              console.error(error);
            });
        }
        this.isChangingBearbeitungsData = false;
      }
    },
    changeFormVariables(changedFormVariables) {
      this.formVariables = changedFormVariables;
    },
    goUpOneRow() {
      if (!this.isLastAbfrageLoading && !this.isFlugplanungsdatenLoading) {
        this.$emit('rowSelectionChanged', 'up');
      }
    },
    goDownOneRow() {
      if (!this.isLastAbfrageLoading && !this.isFlugplanungsdatenLoading) {
        this.$emit('rowSelectionChanged', 'down');
      }
    },
    handleKeyPress($event) {
      if (!this.isLastAbfrageLoading && !this.isFlugplanungsdatenLoading) {
        if ($event.key === 'ArrowLeft') {
          this.goUpOneRow();
          this.isAufgabenCollapsed = false;
          this.$refs.aufgabenSection?.close();
          this.aufgaben = { values: [], isLoaded: false };
        } else if ($event.key === 'ArrowRight') {
          this.goDownOneRow();
          this.isAufgabenCollapsed = false;
          this.$refs.aufgabenSection?.close();
          this.aufgaben = { values: [], isLoaded: false };
        }
      }
    },
    getUrsache(ursache) {
      const ursacheItem = ursachenPossibilities
        .map(item => item.options)
        .flat()
        .find(item => item.enum === ursache);
      return ursacheItem ? ursacheItem : null;
    },
    getLoesungsansatz(loesungsansatz) {
      const loesungsansatzItem = loesungsansatzOptions.find(item => item.enum === loesungsansatz);
      return loesungsansatzItem.label;
    },
    getAkzeptanzgrund(akzeptanzgrund) {
      const akzeptanzgrundItem = this.akzeptanzgrundOptions.find(item => item.enum === akzeptanzgrund);
      return akzeptanzgrundItem?.label;
    },
    getBearbeitungsstatus(bearbeitungstatusEnum) {
      return this.bearbeitungStatusOptions.find(item => item.enum === bearbeitungstatusEnum) || null;
    },
  },
};
</script>

<style scoped>
:deep(.b-sidebar) {
  border-radius: var(--radius-lg) 0 0 var(--radius-lg);
}
.sidebar-content {
  width: 100%;
  padding: 24px;
}
.sidebar-content :deep(.sidebar-field-row__value h6) {
  margin: 0;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}
.sidebar-content :deep(.sidebar-field-control__text) {
  white-space: normal;
}
.sidebar-content :deep(.fvc-fehler) {
  color: var(--danger);
}
.fvc-hinweis {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid var(--border);
  border-radius: 0 0 var(--radius-md) var(--radius-md);
  font-size: 13px;
  color: var(--restriktion-keine-abreise-text);
  background-color: var(--restriktion-keine-abreise-bg);
}
.fvc-leer {
  padding: 24px;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-muted);
}
</style>
